"use client"

import type { VariantProps } from "class-variance-authority"
import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { cva } from "class-variance-authority"
import { IconChevronDownOutline18 as ChevronDown } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

const Accordion = AccordionPrimitive.Root

const accordionTriggerVariants = cva(
	"flex flex-1 items-center justify-between font-medium !outline-none transition-all [&[data-state=open]>svg]:rotate-180",
	{
		variants: {
			variant: {
				default: "py-3 hover:underline",
				dense: "py-0",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
)

const accordionContentVariants = cva("", {
	variants: {
		variant: {
			default: "pb-4 pt-0",
			dense: "py-0",
		},
	},
	defaultVariants: {
		variant: "default",
	},
})

const AccordionItem = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
	<AccordionPrimitive.Item
		ref={ref}
		className={cn("border-b", className)}
		{...props}
	/>
))

interface AccordionTriggerProps
	extends
		React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
		VariantProps<typeof accordionTriggerVariants> {}

const AccordionTrigger = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Trigger>,
	AccordionTriggerProps
>(({ className, children, variant, ...props }, ref) => (
	<AccordionPrimitive.Header className="flex">
		<AccordionPrimitive.Trigger
			ref={ref}
			className={cn(accordionTriggerVariants({ variant, className }))}
			{...props}
		>
			{children}
			<ChevronDown className="h-3 w-3 shrink-0 text-secondary transition-transform !duration-150 !ease-in-out" />
		</AccordionPrimitive.Trigger>
	</AccordionPrimitive.Header>
))

interface AccordionContentProps
	extends
		React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>,
		VariantProps<typeof accordionContentVariants> {}

const AccordionContent = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Content>,
	AccordionContentProps
>(({ className, children, variant, ...props }, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className={cn(
			"overflow-hidden text-sm transition-all !duration-150 !ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
			accordionContentVariants({ variant }),
			className,
		)}
		{...props}
	>
		{children}
	</AccordionPrimitive.Content>
))

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
